import React, { useState } from 'react';
import { MockDataProvider, useMockData } from './MockDataContext';
import { Menu, ChevronLeft, FileText, ChevronDown } from 'lucide-react';

// Import the unified Orders Page
import MockOrdersPage from './MockOrdersPage';

const MockSidebar = ({ collapsed, toggleCollapsed, role, activeMenu, setActiveMenu }) => {
  const [ordersExpanded, setOrdersExpanded] = useState(true);

  return (
    <div
      className={`
        fixed left-0 top-0 h-full z-40 transition-all duration-300 ease-in-out
        ${collapsed ? 'w-16' : 'w-64'}
        flex flex-col bg-gradient-to-b from-blue-600 to-blue-800 shadow-lg text-white
      `}
    >
      {/* Sidebar Header/Toggle */}
      <div className="h-14 flex items-center justify-center border-b border-blue-500/30 p-2">
         <button 
            onClick={toggleCollapsed}
            className="p-2 rounded-lg hover:bg-white/10 transition-colors w-full flex justify-center"
         >
            {collapsed ? <Menu size={20} /> : <div className="flex items-center w-full px-2"><ChevronLeft size={20} className="mr-2" /><span className="font-bold">Menu</span></div>}
         </button>
      </div>

      {/* Nav Items */}
      <nav className="flex-1 p-3 space-y-1 mt-4">
         {/* Orders Menu with Submenu */}
         <div>
            <button 
               onClick={() => !collapsed && setOrdersExpanded(!ordersExpanded)}
               className={`w-full flex items-center p-3 rounded-lg cursor-pointer transition-all bg-white/20 text-white shadow-sm ${collapsed ? 'justify-center' : 'justify-between'}`}
            >
               <div className="flex items-center">
                  <FileText size={20} />
                  {!collapsed && <span className="ml-3 font-medium text-sm">Orders</span>}
               </div>
               {!collapsed && <ChevronDown size={16} className={`transition-transform ${ordersExpanded ? 'rotate-180' : ''}`} />}
            </button>
            
            {/* Submenu */}
            {!collapsed && ordersExpanded && (
               <div className="ml-8 mt-2 space-y-1">
                  <button 
                     onClick={() => setActiveMenu('bulk')}
                     className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-all ${activeMenu === 'bulk' ? 'bg-white/15 text-white' : 'text-blue-200 hover:bg-white/10 hover:text-white'}`}
                  >
                     Bulk Orders
                  </button>
                  <button 
                     onClick={() => setActiveMenu('live')}
                     className={`w-full text-left px-3 py-2 rounded-lg text-sm font-bold transition-all flex items-center ${activeMenu === 'live' ? 'bg-white/15 text-white' : 'text-blue-200 hover:bg-white/10 hover:text-white'}`}
                  >
                     Live Stamping
                     <span className="ml-2 relative flex h-2 w-2">
                        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-orange-400 opacity-75"></span>
                        <span className="relative inline-flex rounded-full h-2 w-2 bg-orange-500"></span>
                     </span>
                  </button>
               </div>
            )}
         </div>
      </nav>

      {/* Role Badge in Sidebar */}
      {!collapsed && (
          <div className="p-4 bg-black/20 m-2 rounded-xl">
              <div className="text-xs text-blue-200 uppercase font-bold tracking-wider">Current Role</div>
              <div className="font-bold text-white capitalize">{role}</div>
          </div>
      )}
    </div>
  );
};

const MockHeader = ({ role, setRole }) => {
  return (
    <div className="h-14 bg-white border-b border-gray-200 flex items-center justify-between px-6 shadow-sm sticky top-0 z-30">
        {/* Left: Logo Area */}
        <div className="flex items-center">
            <div className="font-bold text-xl text-gray-800 mr-8 flex items-center">
                <span className="bg-clip-text text-transparent bg-gradient-to-r from-blue-600 to-indigo-600">
                    MATRICA
                </span>
                <span className="ml-2 text-xs bg-yellow-100 text-yellow-700 px-2 py-0.5 rounded border border-yellow-200">Mock</span>
            </div>
        </div>

        {/* Center: Role Switcher */}
        <div className="bg-gray-100 p-1 rounded-lg flex space-x-1">
            {['admin', 'client', 'vendor'].map(r => (
                <button
                    key={r}
                    onClick={() => setRole(r)}
                    className={`px-3 py-1 text-xs font-bold uppercase rounded-md transition-all ${role === r ? 'bg-white text-indigo-600 shadow-sm' : 'text-gray-400 hover:text-gray-600'}`}
                >
                    {r}
                </button>
            ))}
        </div>

        {/* Right: User Profile */}
        <div className="flex items-center space-x-2">
            <div className="text-right hidden md:block">
                <div className="text-sm font-bold text-gray-700 capitalize">Mock {role}</div>
                <div className="text-xs text-gray-400">demo@example.com</div>
            </div>
            <div className="h-9 w-9 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-full flex items-center justify-center text-white font-bold shadow-md">
                {role.charAt(0).toUpperCase()}
            </div>
        </div>
    </div>
  );
};

const MockLayout = () => {
  const [collapsed, setCollapsed] = useState(false);
  
  return (
    <MockDataProvider>
      <LayoutContent collapsed={collapsed} setCollapsed={setCollapsed} />
    </MockDataProvider>
  );
};

const LayoutContent = ({ collapsed, setCollapsed }) => {
    const { currentUserRole, setCurrentUserRole } = useMockData();
    const [activeMenu, setActiveMenu] = useState('live'); // Default to Live Stamping submenu

    return (
        <div className="min-h-screen bg-gray-50 font-sans text-gray-900 flex">
            {/* Fixed Sidebar */}
            <MockSidebar 
                collapsed={collapsed} 
                toggleCollapsed={() => setCollapsed(!collapsed)}
                role={currentUserRole}
                activeMenu={activeMenu}
                setActiveMenu={setActiveMenu}
            />

            {/* Main Content Wrapper */}
            <div className={`flex-1 flex flex-col transition-all duration-300 ${collapsed ? 'ml-16' : 'ml-64'}`}>
                <MockHeader role={currentUserRole} setRole={setCurrentUserRole} />
                
                <div className="flex-1 overflow-x-hidden">
                    {/* Always show MockOrdersPage, it handles its own tabs internally */}
                    <MockOrdersPage />
                </div>
            </div>
        </div>
    );
};

export default MockLayout;
